import { resolveApiUrl } from "~/utils/url/urlResolver";
import { getCsrfValue } from "~/api/ApiHelper";
import dayjs from "dayjs";
import type { FilterNames } from "../MMDDialog";

type DropdownEntryData = {
  value: string;
  disabled?: boolean;
};

export interface MMDAssistantRequestData {
  marketDesignation: string;
  designations: string[];
  packaging: string[];
  customerMaterialNumbers: string[];
  quantity: number;
  rdd: Date | null;
  priceReference: string;
  filterOrder: FilterNames[];
}

export interface MMDAssistantResponseDTO {
  results: MMDAssistantCalculationResultDTO[];
  filterOptions: {
    products: DropdownEntryData[];
    packaging: DropdownEntryData[];
    customerMaterialNumbers: DropdownEntryData[];
  };
}

export interface MMDAssistantResponseItemDTO {
  estimatedShippingDate: string; // "YYYY-MM-DD"
  productCode: string;
  productDesignation: string;
  sapMaterialNumber: string;
  packCode: string;
  packSize: number;
  customerMaterialNumber: string[];
  quantity: number;
  replenishmentAmount?: number;
}

interface MMDAssistantRequestApiDTO {
  marketDesignation: string;
  quantity: number;
  rdd: string | null;
  filters: {
    field: string;
    values: string[];
  }[];
}

export interface MMDAssistantCalculationResultDTO {
  title: string;
  tag?: string;
  data: MMDAssistantResponseItemDTO[];
}

export interface MMDAssistantApiResponseDTO {
  results: MMDAssistantCalculationResultDTO[];
  filters: {
    [key: string]: {
      field: string;
      values: DropdownEntryData[];
    };
  };
}

/**
 * Fetches MMD Assistant data from the server
 * @param data The form data to send to the server
 * @returns Promise with the MMD table data
 */
export const fetchMMDAssistantData = async (data: MMDAssistantRequestData): Promise<MMDAssistantResponseDTO> => {
  const url = resolveApiUrl("/api/mmd/availabilitySimulator");

  const apiData: MMDAssistantRequestApiDTO = {
    marketDesignation: data.marketDesignation,
    quantity: data.quantity,
    rdd: data.rdd ? dayjs(data.rdd).format("YYYY-MM-DD") : null,
    filters: data.filterOrder.map((filterName) => ({
      field: filterName,
      values: data[filterName],
    })),
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        CSRFToken: getCsrfValue(),
      },
      body: JSON.stringify(apiData),
      credentials: "same-origin",
      mode: "cors",
      referrer: "no-referrer",
    });

    if (!response.ok) {
      throw new Error(`MMD Assistant API error: ${response.status}`);
    }

    const resultData = (await response.json()) as MMDAssistantApiResponseDTO;
    const filterOptionsMap = resultData.filters;
    return {
      results: resultData.results,
      filterOptions: {
        products: filterOptionsMap["designations"]?.values ?? [],
        packaging: filterOptionsMap["packaging"]?.values ?? [],
        customerMaterialNumbers: filterOptionsMap["customerMaterialNumbers"]?.values ?? [],
      },
    };
  } catch (error) {
    console.error("Error fetching MMD Assistant data:", error);
    throw error;
  }
};
